import { Link } from 'react-router-dom'
import { Badge } from '../components/Badge'
import { Card } from '../components/Card'
import { useDb } from '../state/DbProvider'
import {
  agenciesForSuperAgency,
  connectorsForAgency,
  connectorsVisibleToPartner,
  dealsVisibleToPartner,
  getCurrentUser,
  getSuperAgencyForAgency,
  isAgencyLikeRole,
  sumAgencyRewards,
  sumConnectorRewards,
  sumConnectorSales,
  sumPartnerManagedSales,
} from '../state/selectors'
import { formatJPY } from '../utils/format'

function sumMyAllocations(db: NonNullable<ReturnType<typeof useDb>['db']>, userId: string, status?: string) {
  return db.transactions
    .flatMap((transaction) => transaction.allocations)
    .filter(
      (allocation) =>
        allocation.recipientType === 'ユーザー報酬'
        && allocation.userId === userId
        && (status ? allocation.status === status : true),
    )
    .reduce((sum, allocation) => sum + allocation.amountJPY, 0)
}

export default function DashboardPage() {
  const { db } = useDb()
  if (!db) return null

  const me = getCurrentUser(db)
  if (!isAgencyLikeRole(me.role) && me.role !== 'コネクター') {
    return (
      <Card title="ホーム" subtitle="このページは総代理店 / 代理店 / コネクター向けです">
        <div className="px-5 py-4 text-sm text-slate-600">サイドバーから対象メニューへ移動してください。</div>
      </Card>
    )
  }

  const myPending = sumMyAllocations(db, me.id, '未確定')
  const myConfirmed = sumMyAllocations(db, me.id, '確定')
  const myPaid = sumMyAllocations(db, me.id, '支払済み')
  const openDeals =
    me.role === 'コネクター'
      ? db.deals.filter((deal) => deal.connectorId === me.id && !deal.locked && deal.status !== '失注').length
      : dealsVisibleToPartner(db, me.id).filter((deal) => !deal.locked && deal.status !== '失注').length

  const directTeamCount = isAgencyLikeRole(me.role) ? connectorsForAgency(db, me.id).length : 0
  const managedConnectorCount = me.role === '総代理店' ? connectorsVisibleToPartner(db, me.id).length : directTeamCount
  const subordinateAgencyCount = me.role === '総代理店' ? agenciesForSuperAgency(db, me.id).length : 0
  const parentSuperAgency = me.role === '代理店' ? getSuperAgencyForAgency(db, me.id) : undefined

  return (
    <div className="grid gap-6">
      <Card
        title={
          me.role === '総代理店'
            ? `${me.name} さん（総代理店）`
            : me.role === '代理店'
              ? `${me.name} さん（代理店）`
              : `${me.name} さん（コネクター）`
        }
        subtitle="進行中の商談と報酬の状況をまとめて確認できます。入金確認後に確定した報酬は入金待ちとして反映されます。"
      >
        <div className="grid gap-3 px-5 py-4 md:grid-cols-4">
          <SummaryCard label="未確定報酬" value={myPending} />
          <SummaryCard label="入金待ち" value={myConfirmed} />
          <SummaryCard label="支払済み" value={myPaid} />
          <SummaryCard label="進行中の商談" valueText={`${openDeals}件`} />
        </div>

        {me.role === '総代理店' ? (
          <div className="mx-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">総代理店サマリー</div>
                <div className="text-xs text-slate-600">
                  配下代理店数: {subordinateAgencyCount} / 直属コネクター数: {directTeamCount}
                </div>
              </div>
              <Badge tone="slate">総代理店報酬</Badge>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">配下含む担当コネクター数</span>
                <span className="font-semibold">{managedConnectorCount}人</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">管理売上</span>
                <span className="font-semibold">{formatJPY(sumPartnerManagedSales(db, me.id))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">総代理店報酬累計</span>
                <span className="font-semibold">{formatJPY(sumAgencyRewards(db, me.id))}</span>
              </div>
            </div>
          </div>
        ) : me.role === '代理店' ? (
          <div className="mx-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">代理店サマリー</div>
                <div className="text-xs text-slate-600">配下コネクター数: {directTeamCount}</div>
              </div>
              <Badge tone="slate">代理店報酬</Badge>
            </div>
            {parentSuperAgency ? (
              <div className="mt-2 text-xs text-slate-600">
                所属総代理店: <span className="font-semibold">{parentSuperAgency.name}</span>
              </div>
            ) : null}
            <div className="mt-3 grid gap-2 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">チーム売上</span>
                <span className="font-semibold">{formatJPY(sumPartnerManagedSales(db, me.id))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">代理店報酬累計</span>
                <span className="font-semibold">{formatJPY(sumAgencyRewards(db, me.id))}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">コネクターサマリー</div>
                <div className="text-xs text-slate-600">自分が登録した案件の成果を確認できます。</div>
              </div>
              <Badge tone="slate">コネクター報酬</Badge>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">累計売上</span>
                <span className="font-semibold">{formatJPY(sumConnectorSales(db, me.id))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">報酬累計</span>
                <span className="font-semibold">{formatJPY(sumConnectorRewards(db, me.id))}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-3 px-5 py-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-900">クイックアクション</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <DashboardLink to="/marketplace" label="商品一覧" />
              <DashboardLink to="/deals" label="商談管理" primary />
              {me.role === '総代理店' ? <DashboardLink to="/partners" label="パートナー管理" /> : null}
              <DashboardLink to="/network" label="チーム" />
              <DashboardLink to="/rewards" label="報酬 / 出金" />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-900">メモ</div>
            <ul className="mt-3 list-disc pl-5 text-sm text-slate-700">
              <li>コネクターは商談登録フォームから案件を登録し、進捗は閲覧のみできます。</li>
              <li>代理店は配下コネクター案件と代理店直案件を管理できます。</li>
              <li>総代理店は直属コネクター案件と配下代理店案件の両方を管理できます。</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  )
}

function SummaryCard(props: { label: string; value?: number; valueText?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{props.label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">
        {props.valueText ?? formatJPY(props.value ?? 0)}
      </div>
    </div>
  )
}

function DashboardLink(props: { to: string; label: string; primary?: boolean }) {
  return (
    <Link
      to={props.to}
      className={
        props.primary
          ? 'rounded-lg border border-slate-200 bg-[var(--jnavi-navy)] px-3 py-2 text-sm text-white hover:opacity-90'
          : 'rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50'
      }
    >
      {props.label}
    </Link>
  )
}
